import React, { useEffect, useState } from 'react';
import {
  calculateSupportResistanceTable,
  CalculationResult,
} from '../composables/calculateSupportResistance';
import { fetchPolygonSnapshotPriceses } from '../composables/fetchPolygon';
// ✅ 1. นำเข้า Context แทนการเรียก API ตรงๆ
import { useMarketPrices } from '../context/MarketPriceContext';
import { isCryptoSymbol } from '../composables/fetchCrypto';
import { formatCryptoUsd } from '../utils/cryptoDecimal';
interface TableCalculateProps {
  supportResistanceData: { tag: string; price: number | null }[];
  // currentPrice: number | null; // รับราคาปัจจุบันเพิ่มเข้ามา
  symbol: string; // รับชื่อหุ้น เช่น "AAPL"
}

const TableCalculate: React.FC<TableCalculateProps> = ({symbol, supportResistanceData }) => {
  const [invest, setInvest] = useState<number>(0);
  // ราคาแนวรับ/แนวต้าน: หุ้น 2 ตำแหน่ง / คริปโตราคาต่ำกว่า $1 (เช่น DOGE) ต้องเห็นทศนิยมมากกว่านั้น
  const priceText = (n: number) =>
    isCryptoSymbol(symbol) ? formatCryptoUsd(n) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const [isRemembered, setIsRemembered] = useState<boolean>(false);
// 1. เพิ่ม State สำหรับ S0
  const [useS0AsSupport, setUseS0AsSupport] = useState<boolean>(false);
// ✅ 2. เรียกใช้ข้อมูลและฟังก์ชันจาก Context
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();
  
  // สร้างตัวแปรมารับค่าราคาปัจจุบันจาก Context (ถ้ายังไม่มีให้เป็น null)
  const currentPrice = currentPrices[symbol] || null;
  const [isLoadingPrice, setIsLoadingPrice] = useState<boolean>(false);

// ✅ 3. สั่งให้ Context ดึงราคาเมื่อโหลดคอมโพเนนต์
  // (ถ้าใน Context มีราคาหุ้นตัวนี้อยู่แล้ว มันจะข้ามการดึง API ให้เองอัตโนมัติ!)
  useEffect(() => {
    if (symbol) {
      fetchPricesForSymbols([symbol]);
    }
  }, [symbol, fetchPricesForSymbols]);

// // --- 1. useEffect สำหรับดึงราคาปัจจุบันจาก API ---
//   useEffect(() => {
//     const getPrice = async () => {
//       if (!symbol) return;
      
//       setIsLoadingPrice(true);
//       try {
//         const priceMap = await fetchPolygonSnapshotPriceses([symbol]);
//         if (priceMap[symbol]) {
//           setCurrentPrice(priceMap[symbol]);
//         }
//       } catch (error) {
//         console.error("Failed to fetch price:", error);
//       } finally {
//         setIsLoadingPrice(false);
//       }
//     };

//     getPrice();
//     // ถ้าอยากให้ราคาอัปเดตเรื่อยๆ สามารถใช้ setInterval เพิ่มเติมที่นี่ได้
//   }, [symbol]);



  // 2. โหลดค่าจาก Local Storage เมื่อ Mount (รวม S0 ด้วย)
  useEffect(() => {
    const savedInvest = localStorage.getItem('saved_invest_amount');
    const savedCheckbox = localStorage.getItem('is_invest_remembered');
    const savedS0Config = localStorage.getItem('use_s0_as_support'); // โหลดค่า S0

    if (savedCheckbox === 'true' && savedInvest !== null) {
      setInvest(Number(savedInvest));
      setIsRemembered(true);
    }

    if (savedS0Config === 'true') {
      setUseS0AsSupport(true);
    }
  }, []);  // 2. useEffect สำหรับโหลดค่าจาก Local Storage เมื่อ Component Mount ครั้งแรก
  

  // 3. useEffect สำหรับบันทึกหรือลบค่าใน Local Storage เมื่อมีการเปลี่ยนแปลง
  useEffect(() => {
    // จัดการส่วนของเงินลงทุน (เดิม)
    if (isRemembered) {
      localStorage.setItem('saved_invest_amount', invest.toString());
      localStorage.setItem('is_invest_remembered', 'true');
    } else {
      localStorage.removeItem('is_invest_remembered');
    }

    // จัดการส่วนของ S0 (ใหม่)
    if (useS0AsSupport) {
      localStorage.setItem('use_s0_as_support', 'true');
    } else {
      localStorage.setItem('use_s0_as_support', 'false');
      // หรือ localStorage.removeItem('use_s0_as_support');
    }
  }, [invest, isRemembered, useS0AsSupport]); // เพิ่ม useS0AsSupport ใน dependency array

  
// --- ปรับปรุงการแยกข้อมูลแนวรับ-แนวต้านให้เป็นแบบ Dynamic เทียบราคาปัจจุบันจริง ---

// 1. ดึงราคาปัจจุบันมาเป็นเกณฑ์ (ถ้ายังไม่มีราคาจาก API ให้ Fallback เป็น 0 ไปก่อนเพื่อไม่ให้โค้ดพัง)
// ใน TableCalculate.tsx บรรทัดที่ 70 เป็นต้นไป
// ใน TableCalculate.tsx
const marketPrice = currentPrice ?? 0;

// 1. กรองเส้นทั้งหมดที่ "ต่ำกว่าหรือเท่ากับ" ราคาตลาดปัจจุบัน -> บังคับรันชื่อป้ายกำกับเป็น S1, S2, S3... ใหม่ทันที
const rawSupportData = supportResistanceData
  .filter((item) => item.price !== null && item.price <= marketPrice)
  .sort((a, b) => (b.price ?? 0) - (a.price ?? 0)) // เรียงจากแพงไปถูก
  .map((item, idx) => ({
    tag: `S${idx + 1}`, // 🔥 บังคับจ่ายยศแนวรับใหม่ตรงนี้เลย ไม่สนชื่อ Tag เดิมที่ส่งมา
    price: item.price as number
  }));

// รวม S0 ตาม Logic เดิมของพี่อั้ม
const supportData = [...rawSupportData];
if (useS0AsSupport) {
  supportData.unshift({ tag: 'S0', price: marketPrice });
}

// 2. กรองเส้นทั้งหมดที่ "สูงกว่า" ราคาตลาดปัจจุบัน -> บังคับรันชื่อเป็น R1, R2, R3... ใหม่ทันที
const resistanceData = supportResistanceData
  .filter((item) => item.price !== null && item.price > marketPrice)
  .sort((a, b) => (a.price ?? 0) - (b.price ?? 0)) // เรียงจากถูกไปแพง (ตัวใกล้ราคาปัจจุบันที่สุดคือ R1)
  .map((item, idx) => ({
    tag: `R${idx + 1}`, // 🔥 บังคับจ่ายยศแนวต้านใหม่ตรงนี้เลย
    price: item.price as number
  }));

// สั่งคำนวณตารางตามปกติ
const tableData = calculateSupportResistanceTable(invest, supportData, resistanceData);
  return (
    <div
      className="w-full max-w-lg mx-auto px-4 rounded-lg shadow-lg"
      style={{ backgroundColor: 'var(--component-bg-color)', color: 'var(--text-color)' }}
    >
      <div className="flex flex-row items-center justify-between mb-2">
        {/* ส่วนซ้าย: Label หลัก */}
        <label className="text-sm font-medium" style={{ color: 'var(--text-color)' }}>
          ใส่เงินลงทุนเพิ่ม <span style={{ color: 'var(--yellow-text-color)' }}>(USD)</span>
        </label>

        {/* ส่วนขวา: Checkbox จดจำ */}
        <div className="flex items-center">
          <input
            id="remember-invest"
            type="checkbox"
            className="w-4 h-4 rounded cursor-pointer"
            checked={isRemembered}
            onChange={(e) => setIsRemembered(e.target.checked)}
            style={{ accentColor: 'var(--yellow-text-color)' }}
          />
          <label
            htmlFor="remember-invest"
            className="ml-2 text-xs cursor-pointer"
            style={{ color: 'var(--text-color)' }}
          >
            จดจำจำนวนเงินนี้ไว้
          </label>
        </div>
        <div className="flex items-center">
            <input
              id="use-s0"
              type="checkbox"
              className="w-4 h-4 rounded cursor-pointer"
              checked={useS0AsSupport}
              onChange={(e) => setUseS0AsSupport(e.target.checked)}
              // disabled={currentPrice === null} // ปิดถ้าไม่มีราคาปัจจุบัน
              style={{ accentColor: 'var(--yellow-text-color)' }}
            />
            <label htmlFor="use-s0" className="ml-2 text-xs cursor-pointer">
              ใช้ราคาปัจจุบันเป็น <span className="font-bold" style={{ color: 'var(--yellow-text-color)' }}>S0</span>
            </label>
          </div>
      </div>


      <div className="flex items-center mb-2">
        <span className="mr-2" style={{ color: 'var(--text-color)' }}>$</span>
        <input
          type="number"
          className="w-full px-3 py-2 rounded"
          style={{
            backgroundColor: 'var(--bg-item)',
            color: 'var(--text-color)',
            border: '1px solid var(--button-bg-color)',
          }}
          value={invest || ''}
          placeholder="0"
          onChange={(e) => setInvest(Number(e.target.value))}
        />
      </div>


      <div className="rounded-lg p-2 overflow-x-auto custom-scrollbar pb-3" style={{ backgroundColor: 'var(--component-bg-color)' }}>
        {supportResistanceData.length === 0 && !useS0AsSupport ? (
          <div className="text-center" style={{ color: 'var(--text-color)' }}>
            ยังไม่มีข้อมูล โปรดเพิ่ม S,R
          </div>
        ) : (
          <table className="table-auto border-collapse  w-full text-center">
            <thead>
              <tr>
                <th
                  className="font-bold  px-4 py-3 rounded-tl-lg bg-[#232323]"
                  style={{
                    color: 'var(--yellow-text-color)'

                    ,
                  }}
                >
                  ซื้อที่
                </th>
                {resistanceData.map((resistance) => (
                  <th
                    key={resistance.tag}
                    className="font-bold px-2 py-3"
                    style={{
                      backgroundColor: 'var(--bg-item)',
                      color: 'var(--purple-theme)',
                      border: '2px solid var(--secondary-text-color)',
                    }}
                  >
                    {resistance.tag}
                    <br />
                    <span
                      className="font-normal"
                      style={{ color: 'var(--purple-theme-light)' }}
                    >
                      ${priceText(resistance.price)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => {
                const isS0 = row.tag === 'S0';
                return (
                  <tr key={rowIndex} style={isS0 ? { backgroundColor: 'rgba(255, 193, 7, 0.1)' } : {}}>
                    <td
                      className="px-2 py-1 font-bold text-sm"
                      style={{
                        color: isS0 ? '#000' : 'var(--yellow-text-color)',
                        border: '2px solid var(--secondary-text-color)',
                        backgroundColor: isS0 ? 'var(--yellow-text-color)' : 'var(--button-userbox)', // แถว S0 จะมีหัวแถวสีเหลืองเด่น
                      }}
                    >
                      {row.tag}
                      <br />
                      <span className="font-normal" style={{ color: isS0 ? '#000' : 'var(--yellow-theme-light)' }}>
                        ${priceText(row.price)}
                      </span>
                    </td>
                    {row.values.map((value, colIndex) => (
                      <td
                        key={colIndex}
                        className="px-2 py-2"
                        style={{ 
                          border: '2px solid var(--secondary-text-color)',
                          backgroundColor: isS0 ? 'rgba(255, 193, 7, 0.05)' : 'transparent' // พื้นหลังในแถว S0 จะสว่างกว่าปกติเล็กน้อย
                        }}
                      >
                        <div className="font-bold" style={{ color: value.investmentReturn >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)' }}>
                          {value.investmentReturn >= 0 ? '+' : ''}${value.investmentReturn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs" style={{ color: value.percentageChange >= 0 ? 'var(--green-theme-light)' : 'var(--red-theme-light)' }}>
                          ({value.percentageChange >= 0 ? '+' : ''}{value.percentageChange.toFixed(2)}%)
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TableCalculate;

